import type { ReactNode } from 'react';
import { Volume2 } from 'lucide-react';
import type { LocalPlayerClaim } from '../../utils/localFileLevels';

interface LocalFilePlayerStripProps {
  claim: LocalPlayerClaim;
  blocked: boolean;
  onClaim(): void;
  onUnblock(): void;
  children?: ReactNode;
}

function needsAudioUnlock(blocked: boolean): boolean {
  if (blocked) return true;
  const activation = (navigator as Navigator & { userActivation?: { hasBeenActive: boolean } }).userActivation;
  return activation?.hasBeenActive === false;
}

const BOX_STYLES: Record<LocalPlayerClaim, string> = {
  'this-device': 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
  'other-device': 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700',
  none: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
};

function DeviceLine({ claim, blocked, onClaim, onUnblock }: Omit<LocalFilePlayerStripProps, 'children'>) {
  if (claim === 'this-device') {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Volume2 className="w-4 h-4 text-green-500 flex-shrink-0" />
        <span className="text-xs font-medium text-green-700 dark:text-green-400">
          Local files play on this device. Keep this page open.
        </span>
        {needsAudioUnlock(blocked) && (
          <button
            onClick={onUnblock}
            className="ml-auto px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary text-white hover:bg-primary/90 whitespace-nowrap"
          >
            Click to allow local file audio
          </button>
        )}
      </div>
    );
  }

  if (claim === 'other-device') {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Volume2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span className="text-xs text-gray-500 dark:text-gray-400">Local files play on another device.</span>
        <button
          onClick={onClaim}
          className="ml-auto text-xs text-primary hover:text-primary/80 underline whitespace-nowrap"
        >
          Play here
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Volume2 className="w-4 h-4 text-amber-500 flex-shrink-0" />
      <span className="text-xs font-medium text-amber-700 dark:text-amber-400">No device is set to play local files.</span>
      <button
        onClick={onClaim}
        className="ml-auto text-xs text-amber-600 dark:text-amber-400 hover:text-primary underline whitespace-nowrap"
      >
        Play on this device
      </button>
    </div>
  );
}

export function LocalFilePlayerStrip({ children, ...deviceProps }: LocalFilePlayerStripProps) {
  return (
    <div
      data-testid="local-file-player-strip"
      className={`flex flex-col gap-3 px-3 py-2 mb-3 rounded-lg border ${BOX_STYLES[deviceProps.claim]}`}
    >
      <DeviceLine {...deviceProps} />
      {children}
    </div>
  );
}
